import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import type { CrewFile, CrewTransactionRow } from '../types';

export type PrintMetric = {
  label: string;
  value: string;
  hint?: string;
};

function formatYears(value?: number | null) {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatMonths(value?: number | null) {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(Math.round(value));
}

function ageFromBirthDate(birthDate?: string | null) {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let years = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) years -= 1;
  return years >= 0 ? years : null;
}

function daysUntil(iso?: string | null) {
  if (!iso) return null;
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - today.getTime()) / 86_400_000);
}

function daysHint(days: number | null | undefined, overdueWord = 'overdue') {
  if (days == null) return undefined;
  if (days < 0) return `${Math.abs(days)}d ${overdueWord}`;
  return `${days}d left`;
}

function formatWage(tx?: CrewTransactionRow | null) {
  if (tx?.totalWage == null) return null;
  const amount = typeof tx.totalWage === 'number' ? tx.totalWage : Number(tx.totalWage);
  if (!Number.isFinite(amount)) return null;
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return tx.wageCurrency ? `${formatted} ${tx.wageCurrency}` : formatted;
}

export function getCurrentAssignment(transactions?: CrewTransactionRow[]) {
  const list = transactions ?? [];
  const onBoard = list.filter((t) => t.status === 'ONB');
  if (onBoard.length) {
    return onBoard.sort((a, b) => (b.signOnDate ?? '').localeCompare(a.signOnDate ?? ''))[0];
  }
  return list.sort((a, b) =>
    (b.signOnDate ?? b.startDate ?? '').localeCompare(a.signOnDate ?? a.startDate ?? ''),
  )[0];
}

const TENURE_METRICS: Array<{
  key: keyof NonNullable<CrewFile['experience']>;
  label: string;
  format: (v: number) => string;
  unit: string;
}> = [
  { key: 'timeInRankYears', label: 'Time in rank', format: formatYears, unit: 'Years' },
  { key: 'timeWithCompanyYears', label: 'With company', format: formatYears, unit: 'Years' },
  { key: 'timeOnShipTypeYears', label: 'Ship type', format: formatYears, unit: 'Years' },
  { key: 'timeWithMainEngineYears', label: 'Main engine', format: formatYears, unit: 'Years' },
  { key: 'timeOnOtherShipTypesYears', label: 'Other ship types', format: formatYears, unit: 'Years' },
  { key: 'timeOnboardThisTourMonths', label: 'This tour', format: formatMonths, unit: 'Months' },
];

export function buildAtAGlanceMetrics(item: CrewFile): PrintMetric[] {
  const current = getCurrentAssignment(item.transactions);
  const certs = item.certs ?? [];
  const expiring = certs.filter((c) => {
    const d = daysUntil(c.expiredDate);
    return d != null && d >= 0 && d <= 90;
  }).length;
  const expired = certs.filter((c) => {
    const d = daysUntil(c.expiredDate);
    return d != null && d < 0;
  }).length;
  const vesselName = current?.vessel?.vesselName ?? current?.vesselName ?? null;
  const coeDate = current?.coeDate ?? current?.latestCoeDate ?? current?.extCoeDate ?? null;
  const coeDays = daysUntil(coeDate);
  const nextMedical = [...(item.medicals ?? [])]
    .filter((m) => m.nextDueDate)
    .sort((a, b) => (a.nextDueDate ?? '').localeCompare(b.nextDueDate ?? ''))[0];
  const medicalDays = daysUntil(nextMedical?.nextDueDate);
  const wage = formatWage(current);
  const wageRank = current?.rank?.abrv || current?.rank?.name || item.rank?.abrv || item.rank?.name;
  const intention = current?.intentionSignOffDate ?? null;
  const intentionDays = daysUntil(intention);
  const age = ageFromBirthDate(item.birthDate);

  return [
    {
      label: 'Current vessel',
      value: vesselName || '—',
      hint: current?.status === 'ONB' ? 'On board' : current ? 'Off board' : undefined,
    },
    {
      label: 'COE',
      value: coeDate ? formatDateGlobal(coeDate) : '—',
      hint: daysHint(coeDays),
    },
    {
      label: 'Cert health',
      value: certs.length === 0 ? '—' : `${expired} expired · ${expiring} ≤90d`,
      hint: `${certs.length} total`,
    },
    {
      label: 'Medical next due',
      value: nextMedical?.nextDueDate ? formatDateGlobal(nextMedical.nextDueDate) : '—',
      hint:
        medicalDays == null
          ? nextMedical?.kind ?? undefined
          : `${daysHint(medicalDays)}${nextMedical?.kind ? ` · ${nextMedical.kind}` : ''}`,
    },
    {
      label: 'Last wage',
      value: wage || '—',
      hint: wageRank ? `In rank ${wageRank}` : undefined,
    },
    {
      label: 'Availability',
      value: item.availableFrom ? formatDateGlobal(item.availableFrom) : '—',
      hint: item.availableTo
        ? `To ${formatDateGlobal(item.availableTo)}`
        : item.availabilityStatus?.name ?? undefined,
    },
    {
      label: 'Intention S/Off',
      value: intention ? formatDateGlobal(intention) : '—',
      hint: daysHint(intentionDays, 'past'),
    },
    {
      label: 'Age',
      value: age != null ? String(age) : '—',
      hint: item.nationality?.name ?? undefined,
    },
  ];
}

export function buildTenureMetrics(item: CrewFile): PrintMetric[] {
  return TENURE_METRICS.map((metric) => {
    const raw = item.experience?.[metric.key];
    return {
      label: metric.label,
      value: typeof raw === 'number' ? metric.format(raw) : '—',
      hint: metric.unit,
    };
  });
}
